import { type ApolloError } from '@apollo/client';
import { Form, FormSelect, FormTextInput, StandardFormButtons } from '@components/form';
import { useCreateEditorMutation, type Workspace } from '@generated/graphql';
import { MenuItem, Stack } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import posthog from 'posthog-js';
import { useState } from 'react';
import * as yup from 'yup';
import { StorageSizeSelector } from '../StorageSizeSelector';
import { AdvancedSettingsSection } from './AdvancedSettingsSection';
import { DefaultOption, InstanceSelector } from './InstanceSelector';

const validationSchema = yup.object({
  name: yup.string(),
  instanceType: yup.string().required('Instance type is required'),
  newWorkspaceId: yup.string().required('Workspace is required'),
  autoStopEnabled: yup.boolean().required('Auto stop is required'),
  autoStopCpuThreshold: yup.number(),
  autoStopTimeoutMinutes: yup.number(),
  autoDeleteEnabled: yup.boolean().required('Auto delete is required'),
  autoDeleteTimeoutDays: yup.number(),
  autoUpdateEnabled: yup.boolean().required('Auto update is required'),
});

type CreateServerModalProps = {
  organizationId: string;
  workspaceId?: string;
  workspaces?: Workspace[];
  onCreate?: (editorId: string) => void;
  onClose: VoidFunction;
};

export const CreateServerModal = ({
  organizationId,
  workspaceId,
  workspaces,
  onCreate,
  onClose,
}: CreateServerModalProps) => {
  const { createAsyncNotification } = useNotifications();
  const [createServerMutation] = useCreateEditorMutation();
  const [formError, setFormError] = useState<string | null>(null);

  const createServer = async ({
    newWorkspaceId,
    instanceType,
    name,
    storageSize,
    autoStopEnabled,
    autoStopCpuThreshold,
    autoStopTimeoutMinutes,
    autoDeleteEnabled,
    autoDeleteTimeoutDays,
    autoUpdateEnabled,
  }: {
    newWorkspaceId: string;
    instanceType: string;
    name: string;
    storageSize: number;
    autoStopEnabled: boolean;
    autoStopCpuThreshold: number;
    autoStopTimeoutMinutes: number;
    autoDeleteEnabled: boolean;
    autoDeleteTimeoutDays: number;
    autoUpdateEnabled: boolean;
  }) => {
    await createServerMutation({
      variables: {
        organizationId,
        workspaceId: newWorkspaceId,
        instance: instanceType,
        name,
        storageSize,
        autoStopEnabled,
        autoStopCpuThreshold: autoStopEnabled ? autoStopCpuThreshold : undefined,
        autoStopTimeoutMinutes: autoStopEnabled ? autoStopTimeoutMinutes : undefined,
        autoDeleteEnabled,
        autoDeleteTimeoutDays: autoDeleteEnabled ? autoDeleteTimeoutDays : undefined,
        autoUpdateEnabled,
      },
      onCompleted: (data) => {
        posthog.capture('services:user_create-server_submitted');
        onCreate?.(data?.createEditor);
        onClose();
      },
      onError: (error: ApolloError) => {
        setFormError(error.message);
        throw error;
      },
    });
  };

  const handleFormSubmit = createAsyncNotification(
    'New server successfully created',
    createServer,
    {
      loadingMessage: 'Creating server...',
    },
  );

  return (
    <Form
      initialValues={{
        name: '',
        instanceType: DefaultOption,
        newWorkspaceId: workspaceId || '',
        storageSize: 300,
        autoStopEnabled: true,
        autoStopCpuThreshold: 20,
        autoStopTimeoutMinutes: 180,
        autoDeleteEnabled: true,
        autoDeleteTimeoutDays: 14,
        autoUpdateEnabled: true,
      }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
    >
      {() => {
        return (
          <Stack style={{ maxWidth: '378px' }}>
            <FormTextInput
              name="name"
              label="Name"
              labelInfo="An optional name to identify this server."
            />
            {!workspaceId && (
              <FormSelect
                name="newWorkspaceId"
                label="Workspace"
                labelInfo="The workspace this server will be attached to."
              >
                {workspaces.map((workspace) => (
                  <MenuItem key={workspace.workspaceId} value={workspace.workspaceId}>
                    {workspace.name}
                  </MenuItem>
                ))}
              </FormSelect>
            )}
            <InstanceSelector
              name="instanceType"
              label="Instance type"
              labelInfo="The hardware configuration for this server. Choose GPU instances for AI/ML workloads."
            />
            <StorageSizeSelector
              name="storageSize"
              label="Storage size"
              labelInfo="The amount of disk storage available on this server."
            />

            <AdvancedSettingsSection />

            <StandardFormButtons onClose={onClose} formError={formError} submitLabel="Create" />
          </Stack>
        );
      }}
    </Form>
  );
};
